import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Notification } from 'app/layout/components-common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/components-common/notifications/notifications.service';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications'
})
export class NotificationsComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: any;
    unreadCount: number = 0;
    stopTone: boolean = JSON.parse(localStorage.getItem('stopSoundAlarm')) || false;
    private _overlayRef: OverlayRef;
    
    private destroy$ = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _cRef: ChangeDetectorRef
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------
    /**
    * On init
    */
    ngOnInit(): void {
        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this.destroy$))
            .subscribe((notifications: Notification[]) => {
                // Load the notifications
                this.notifications = notifications;

                // Calculate the unread count
                this._calculateUnreadCount();
                if (this._overlayRef) { this._overlayRef.detach(); }
                // Mark for check
                this._cRef.markForCheck();
            });

        //this.notifications = JSON.stringify (sessionStorage.getItem('notificaciones') ) ;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.destroy$.next();
        this.destroy$.complete(); // Libera recursos

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    ngAfterViewChecked(): void {
        this._cRef.detectChanges();
    }


    // -----------------------------------------------------------------------------------------------------
    // @ private methods
    // -----------------------------------------------------------------------------------------------------
    private updateLstStorage(lstAlrmStorage) {
        sessionStorage.removeItem('lstAlarms')
        sessionStorage.setItem('lstAlarms', JSON.stringify(lstAlrmStorage))
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the messages panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        // Mark all as read
        this._notificationsService.markAllAsRead().subscribe();
        let lstAlrmStorage = JSON.parse(sessionStorage.getItem('lstAlarms'))

        lstAlrmStorage.forEach((notification, index) => {
            lstAlrmStorage[index].read = true;
        });
        this.updateLstStorage(lstAlrmStorage)
    }


    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void {
        // Toggle the read status
        notification.read = !notification.read;
        const indexAlrm = this.notifications.findIndex((item) => item.id === notification.id),
            lstAlrmStorage = JSON.parse(sessionStorage.getItem('lstAlarms'));
        lstAlrmStorage[indexAlrm].read = notification.read;

        this.updateLstStorage(lstAlrmStorage)
        this._calculateUnreadCount();
        // Update the notification
        // this._notificationsService.update(notification.id, notification).subscribe();
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void {

        const indexAlrm = this.notifications.findIndex((item) => item.id === notification.id),
            lstAlrmStorage = JSON.parse(sessionStorage.getItem('lstAlarms'));

        // Delete the notification
        this.notifications.splice(indexAlrm, 1);
        lstAlrmStorage.splice(indexAlrm, 1)
        this.updateLstStorage(lstAlrmStorage)
        this._calculateUnreadCount();
        // Delete the notification

    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }


    adjustTone() {
        this.stopTone = !this.stopTone;
        localStorage.setItem('stopSoundAlarm', this.stopTone.toString())
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'cb-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition()
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(notification => !notification.read).length;
        }

        this.unreadCount = count;
        this._cRef.markForCheck();
    }
}
